import {Injectable} from "@angular/core";
import {IReportDefinition, ISelectedItem} from "@models/search.interface";

declare var jmespath: any;

@Injectable()
export class Reports {
  defaultRpt: string = "PropertyOwnershipAndLocationReport";
  /*
  * fieldsByReport Object
  * key: the id used by the Report Dropdown
  * nestedPrime: is the Main Many object to the one account
  * excludes: excludes top level data
  * nestedChildren:
  * title: Report Title
  * nestedTargets: the one=>many relationships for querying & flattening
  * fields:
  *   label displays in the table header
  *   fieldName is used in the flattening query
  *   nestedPrime is the main one-to-many ID
  *   nestedChildren are the sub-arrays under nestedPrime
  *   display means it's for the UI to display in the table
  *   export means it's included in the export
  */
  fieldsByReport: Array<IReportDefinition> = [
    {
      key:"PropertyOwnershipAndLocationReport",
      title:"Property Ownership & Location Report",
      nestedPrime:null,
      innerHits:null,
      nestedChildren:null,
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Situs Street Address", sortName:"primary_address_street_address", fieldName:"primary_address.street_address", width:"15%", order:9, display:true, export:true, format:"default"},
        {label:"Situs City", sortName:"primary_address_city", fieldName:"primary_address.city", order:10, display:false, export:true, format:"default"},
        {label:"Situs Zip Code", sortName:"primary_address_zip_code", fieldName:"primary_address.zip_code", order:11, display:false, export:true, format:"zipcode"},
        {label:"Legal Description", sortName:"legal_description", fieldName:"legal_description", order:12, display:false, export:true, format:"default"},
        {label:"LEA", sortName:"lea", fieldName:"lea", order:13, display:false, export:true, format:"default"},        
        {label:"Account Type", sortName:"account_type", fieldName:"account_type", order:14, display:false, export:true, format:"default"},
        {label:"Subdivision Name", sortName:"subdivision_name", fieldName:"subdivision.name", order:15, display:true, export:false, format:"default"},        
        {label:"Subdivision Filing Number", sortName:"subdivision_filing_number", fieldName:"subdivison.filing.number", order:16, display:false, export:true, format:"default"},
        {label:"Subdivision Reception Number", sortName:"recording_number", fieldName:"recording_number", order:17, display:false, export:true, format:"default"},
        {label:"Actual Value", sortName:"actual_value_sum", fieldName:"actual_value_sum", order:18, display:true, export:true, format:"currency"},
        {label:"Assessed Value", sortName:"assessed_value_sum", fieldName:"assessed_value_sum", order:19, display:true, export:true, format:"currency"},
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number"
            , "primary_address.street_address"
            , "primary_owner.name"
            , "actual_value_sum"
            , "assessed_value_sum"
            , "state_parcel_number"
            , "primary_address.city"
            , "primary_address.zip_code"
            , "primary_owner.name"
            , "primary_owner.city"
            , "primary_owner.address1"
            , "primary_owner.address2"
            , "primary_owner.city"
            , "primary_owner.state"
            , "primary_owner.zip_code"
            , "account_type"
            , "legal_description"
            , "geo_location"
            , "lea"
            , "neighborhood_code"
            , "neighborhood_extension"
            , "subdivision.name"
            , "subdivision.filing.number"
            , "recording_number"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:null,
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"ParcelValueReport",
      title:"Parcel Value Report",
      nestedPrime:"_source.class_code_values",
      innerHits:null,
      nestedChildren:null,
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Situs Street Address", sortName:"primary_address_street_address", fieldName:"primary_address.street_address", width:"15%", order:9, display:true, export:true, format:"default"},
        {label:"Situs City", sortName:"primary_address_city", fieldName:"primary_address.city", order:10, display:false, export:true, format:"default"},
        {label:"Situs Zip Code", sortName:"primary_address_zip_code", fieldName:"primary_address.zip_code", order:11, display:false, export:true, format:"zipcode"},
        {label:"Valuation Class Code", sortName:"valuation_class_code", fieldName:"class_code_values.valuation_class_code", order:12, display:true, export:true, format:"default"},
        {label:"Valuation Class Description",sortName:"valuation_class_code_description",fieldName:"class_code_values.valuation_class_code_description",width:"20%",order:13,display:true,export:true,format:"default"},
        {label:"Actual Value", sortName:"actual_value_sum", fieldName:"class_code_values.actual_value_sum", order:14, display:true, export:true, format:"currency"},
        {label:"Assessed Value", sortName:"assessed_value_sum", fieldName:"class_code_values.assessed_value_sum", order:15, display:true, export:true, format:"currency"},
        {label:"Valuation Type", sortName:"appraisal_type", fieldName:"appraisal_type", order:16, display:false, export:true, format:"default"},
        {label:"Net Acres", sortName:"net_acres_sum", fieldName:"class_code_values.net_acres_sum", order:17, display:true, export:true, format:"default"}
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number",
            "state_parcel_number",
            "primary_owner.name",
            "primary_owner.city",
            "primary_owner.address1",
            "primary_owner.address2",
            "primary_owner.city",
            "primary_owner.state",
            "primary_owner.zip_code",
            "primary_address.street_address",
            "primary_address.city",
            "primary_address.zip_code",
            "appraisal_type",
            "class_code_values.valuation_class_code",
            "class_code_values.valuation_class_code_description",
            "class_code_values.actual_value_sum",
            "class_code_values.assessed_value_sum",
            "class_code_values.net_acres_sum",
            "geo_location"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:null,
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"PropertyImprovementOccupancyReport",
      title:"Property Improvement Occupancy Report",
      nestedPrime:"inner_hits.buildings.hits.hits[*]",
      innerHits:"buildings",
      nestedChildren:"inner_hits.*.hits[].hits[]",
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Situs Street Address", sortName:"primary_address_street_address", fieldName:"primary_address.street_address", width:"15%", order:9, display:true, export:true, format:"default"},
        {label:"Situs City", sortName:"primary_address_city", fieldName:"primary_address.city", order:10, display:false, export:true, format:"default"},
        {label:"Situs Zip Code", sortName:"primary_address_zip_code", fieldName:"primary_address.zip_code", order:11, display:false, export:true, format:"zipcode"},
        {label:"Property Type", sortName:"property_type", fieldName:"property_type", order:12, display:true, export:true, format:"default"},
        {label:"Occupancy Code", sortName:"use_code", fieldName:"use_code", order:13, display:true, export:true, format:"default"},
        {label:"Occupancy Percentage", sortName:"use_percentage", fieldName:"use_percentage", order:14, display:true, export:true, format:"percent"},
        {label:"Occ Code Description", sortName:"use_code_concat", fieldName:"use_code_concat", order:15, display:false, export:true, format:"default"},
        {label:"Condition", sortName:"condition", fieldName:"condition", order:16, display:true, export:true, format:"default"},
        {label:"Quality", sortName:"build_quality", fieldName:"build_quality", order:17, display:true, export:true, format:"default"},
        {label:"Completion Percentage", sortName:"completed_percent", fieldName:"completed_percent", order:18, display:true, export:true, format:"percent"}
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number",
            "primary_address.street_address",
            "state_parcel_number",
            "primary_address.city",
            "primary_address.zip_code",
            "primary_owner.name",
            "primary_owner.address1",
            "primary_owner.address2",
            "primary_owner.city",
            "primary_owner.state",
            "primary_owner.zip_code",
            "geo_location"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:{size:50, _source:{includes:["buildings.property_type", "buildings.condition", "buildings.build_quality", "buildings.completed_percent"]}},
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:{size:50, _source:{includes:["buildings.uses.use_code", "buildings.uses.use_code_concat", "buildings.uses.use_percentage"]}},
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"PropertyImprovementSegmentsReport",
      title:"Property Improvement Segments Report",
      nestedPrime:"inner_hits.buildings.hits.hits[*]",
      innerHits:"buildings",
      nestedChildren:"inner_hits.*.hits[].hits[]",
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Property Type", sortName:"property_type", fieldName:"property_type", width:"15%", order:9, "display":true, export:true, format:"default"},
        {label:"Building #", sortName:"building_number", fieldName:"building_number", order:10, "display":true, export:true, format:"default"},
        {label:"Quality", sortName:"build_quality", fieldName:"build_quality", order:11, "display":true, export:true, format:"default"},
        {label:"Completion Percentage", sortName:"completed_percent", fieldName:"completed_percent", order:12, "display":false, export:true, format:"percent"},
        {label:"Improvement SF", sortName:"above_grade_square_feet", fieldName:"above_grade_square_feet", order:13, "display":true, export:true, format:"number"},
        {label:"Style", sortName:"style_code_concat", fieldName:"style_code_concat", order:14, "display":true, export:true, format:"default"},
        {label:"Year Built", sortName:"year_built", fieldName:"year_built", order:15, "display":true, export:true, format:"default"},
        {label:"Stories", sortName:"number_of_stories", fieldName:"number_of_stories", order:16, "display":false, export:true, format:"default"},
        {label:"Bedroom Count", sortName:"bedroom_count", fieldName:"bedroom_count", order:17, "display":false, export:true, format:"default"},
        {label:"Bathroom Count", sortName:"bathroom_count", fieldName:"bathroom_count", order:18, "display":false, export:true, format:"default"},
        {label:"Built As SF", sortName:"style_square_feet", fieldName:"style_square_feet", order:19, "display":false, export:true, format:"number"},
        {label:"Roof Type", sortName:"roof_construction_type", fieldName:"roof_construction_type", order:20, "display":false, export:true, format:"default"},
        {label:"Interior Type", sortName:"interior_finish_type", fieldName:"interior_finish_type", order:21, "display":false, export:true, format:"default"},
        {label:"Exterior Type", sortName:"exterior_construction_type", fieldName:"exterior_construction_type", order:22, "display":false, export:true, format:"default"},
        {label:"Heat Type", sortName:"heat_type", fieldName:"heat_type", order:23, "display":false, export:true, format:"default"},
        {label:"Roof Material Type", sortName:"roof_material_type", fieldName:"roof_material_type", order:24, "display":false, export:true, format:"default"},
        {label:"Floor Type", sortName:"floor_material_type", fieldName:"floor_material_type", order:25, "display":false, export:true, format:"default"}
      ],
      queryStructure: {
        acctIncludes: {
          includes: [
            "account_number"
            ,"primary_address.street_address"
            ,"primary_address.city"
            ,"primary_address.zip_code"
            ,"state_parcel_number",
            "primary_owner.name",
            "primary_owner.address1",
            "primary_owner.address2",
            "primary_owner.city",
            "primary_owner.state",
            "primary_owner.zip_code",
            "geo_location"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:{size:50, _source:{includes:["buildings.property_type", "buildings.building_number", "buildings.build_quality", "buildings.completed_percent", "buildings.above_grade_square_feet"]}},
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:{
          size:50,
          _source:{
            includes:[
              "buildings.styles.style_code_concat"
              , "buildings.styles.style_square_feet"
              , "buildings.styles.year_built"
              , "buildings.styles.roof_construction_type"
              , "buildings.styles.roof_material_type"
              , "buildings.styles.heat_type"
              , "buildings.styles.exterior_construction_type"
              , "buildings.styles.interior_finish_type"
              , "buildings.styles.number_of_stories"
              , "buildings.styles.floor_material_type"
              , "buildings.styles.bedroom_count"
              , "buildings.styles.bathroom_count"
            ]
          }
        },
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"BuildingSummaryReport",
      title:"Property Improvement Summary Report",
      nestedPrime:"inner_hits.buildings.hits.hits[*]",
      innerHits:"buildings",
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Property Type", sortName:"property_type", fieldName:"property_type",order:9, display:true, export:true, format:"default"},
        {label:"Building #", sortName:"building_number", fieldName:"building_number", order:10, display:true, export:true, format:"default"},
        {label:"Quality", sortName:"build_quality", fieldName:"build_quality", order:11, display:true, export:true, format:"default"},
        {label:"Completion Percentage", sortName:"completed_percent", fieldName:"completed_percent", order:12, display:false, export:true, format:"percent"},
        {label:"Improvmnt SF", sortName:"above_grade_square_feet", fieldName:"above_grade_square_feet", order:13, display:true, export:true, format:"number"},
        {label:"Unit Type ", sortName:"unit_type", fieldName:"unit_type", order:14, display:false, export:true, format:"default"},
        {label:"Fireplace Count", sortName:"fireplaces", fieldName:"fireplaces", order:15, display:true, export:true, format:"default"},
        {label:"Garage SF", sortName:"garage_total_square_feet", fieldName:"garage_total_square_feet", order:16, display:true, export:true, format:"default"},
        {label:"Basement SF", sortName:"basement_square_feet", fieldName:"basement.square_feet", order:17, display:false, export:true, format:"number"},
        {label:"Finished Basement SF", sortName:"basement_finished_square_feet", fieldName:"basement.finished_square_feet", order:18, display:true, export:true, format:"number"},
        {
          label:"Unfinished Basement SF",
          sortName:"basement_unfinished_square_feet",
          fieldName:"basement_unfinished_square_feet",
          compute:{requires:"basement_square_feet", operation:"-", variables:["basement_square_feet", "basement_finished_square_feet"]},
          order:19,
          display:true,
          export:true,
          format:"number"
        },
        {
          label:"Finished Basement %",
          sortName:"basement_finished_percent",
          fieldName:"basement_finished_percent",
          compute:{requires:"basement_square_feet", operation:"/", variables:["basement_finished_square_feet", "basement_square_feet"]},
          order:20,
          display:false,
          export:true,
          format:"number"
        },
        {
          label:"Unfinished Basement %",
          sortName:"basement_unfinished_percent",
          fieldName:"basement_unfinished_percent",
          compute:{requires:"basement_square_feet", operation:"/", variables:["basement_unfinished_square_feet", "basement_square_feet"]},
          order:21,
          display:false,
          export:true,
          format:"number"
        },
        {label:"Walkout Basement", sortName:"basement_walkout", fieldName:"basement.walkout", order:22, display:true, export:true, format:"default"},
        {label:"Total Porch SF ", sortName:"porch_total_square_feet", fieldName:"porch_total_square_feet", order:23, display:false, export:true, format:"default"}
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number"
            ,"primary_address.street_address" 
            , "primary_address.city"
            , "primary_address.zip_code"                        
            , "state_parcel_number"
            , "completed_percent"
            , "primary_owner.name"
            , "primary_owner.address1"
            , "primary_owner.address2"
            , "primary_owner.city"
            , "primary_owner.state"
            , "primary_owner.zip_code"
            , "geo_location"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:{
          size:50,
          _source:{
            includes:[
              "buildings.build_quality"
              , "buildings.building_number"
              , "buildings.completed_percent"
              , "buildings.porch_total_square_feet"
              , "buildings.fireplaces"
              , "buildings.above_grade_square_feet"
              , "buildings.garage_total_square_feet"
              , "buildings.porch_total_square_feet"
              , "buildings.property_type"
              , "buildings.basement.walkout"
              , "buildings.basement.finished_square_feet"
              , "buildings.basement.square_feet"
              , "buildings.unit_type"
            ]
          }
        },
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"SalesInformationReport",
      title:"Sale Information Report",
      nestedPrime:"inner_hits.sales.hits.hits[]",
      innerHits:"sales",
      excludes:"sales",
      fields:[
        {label:"State Parcel #", sortName:"state_parcel_number", fieldName:"state_parcel_number", order:1, display:false, export:true, format:"default"},
        {label:"Account #", sortName:"account_number", fieldName:"account_number", order:2, display:true, export:true, format:"accountLink"},
        {label:"Owner Name", sortName:"primary_owner_name", fieldName:"primary_owner.name", width:"15%", order:3, display:true, export:true, format:"default"},
        {label:"Owner Street Address", sortName:"primary_owner_address1", fieldName:"primary_owner.address1", order:4, display:false, export:true, format:"default"},
        {label:"Owner Street Address 2", sortName:"primary_owner_address2", fieldName:"primary_owner.address2", order:5, display:false, export:true, format:"default"},
        {label:"Owner City", sortName:"primary_owner_city", fieldName:"primary_owner.city", order:6, display:false, export:true, format:"default"},
        {label:"Owner State", sortName:"primary_owner_state", fieldName:"primary_owner.state", order:7, display:false, export:true, format:"default"},
        {label:"Owner Zip Code", sortName:"primary_owner_zip_code", fieldName:"primary_owner.zip_code", order:8, display:false, export:true, format:"zipcode"},
        {label:"Situs Street Address", sortName:"primary_address_street_address", fieldName:"primary_address.street_address", width:"15%", order:9, display:false, export:true, format:"default"},
        {label:"Situs City", sortName:"primary_address_city", fieldName:"primary_address.city", order:11, display:false, export:true, format:"default"},
        {label:"Situs Zip Code", sortName:"primary_address_zip_code", fieldName:"primary_address.zip_code", order:12, display:false, export:true, format:"zipcode"},
        {label:"Account Type", sortName:"account_type", fieldName:"account_type", order:13, display:false, export:true, format:"default"},
        {label:"Deed Type", sortName:"deed_type", fieldName:"deed_type", order:14, display:true, export:true, format:"default"},
        {label:"Reception #", sortName:"recording_number", fieldName:"recording_number", order:15, display:true, export:true, format:"default"},
        {label:"Sale Date", sortName:"sale_date", fieldName:"sale_date", order:16, display:true, export:true, format:"date"},
        {label:"Sale Price", sortName:"sale_price", fieldName:"sale_price", order:17, display:true, export:true, format:"currency"},
        {label:"Grantor", sortName:"grantor", fieldName:"grantor", order:18, display:true, export:true, format:"default"},
        {label:"Grantee", sortName:"grantee", fieldName:"grantee", order:19, display:true, export:true, format:"default"},
        {label:"Book", sortName:"book", fieldName:"book", order:20, display:false, export:true, format:"default"},
        {label:"Page", sortName:"page", fieldName:"page", order:21, display:false, export:true, format:"default"},
        {label:"Acres", sortName:"net_acres_sum", fieldName:"net_acres_sum", order:22, display:false, export:true, format:"default"},
        {label:"Improved", sortName:"improved", fieldName:"improved",order:23, display:false, export:true, format:"default"},
        {label:"Qualified Sale", sortName:"qualified_sale", fieldName:"qualified_sale",order:24, display:false, export:true, format:"default"},   
        {label:"Multi-Parcel", sortName:"multi_parcel", fieldName:"multi_parcel",order:25, display:false, export:true, format:"default"}
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number",
            "primary_address.street_address",
            "state_parcel_number",
            "primary_address.city",
            "primary_address.zip_code",
            "account_type",
            "primary_owner.name",
            "primary_owner.address1",
            "primary_owner.address2",
            "primary_owner.city",
            "primary_owner.state",
            "primary_owner.zip_code",
            "net_acres_sum",
            "geo_location"       
           ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:null,
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:{
          size:50, 
          _source:{
            includes:[
              "sales.deed_type"
              , "sales.recording_number"
              , "sales.sale_date"
              , "sales.sale_price"
              , "sales.grantor"
              , "sales.grantee"
              , "sales.book"
              , "sales.page"
              , "sales.improved"
              , "sales.qualified_sale"
              , "sales.multi_parcel"
             ]
          }
        },
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    },
    {
      key:"ValuationHistoryReport",
      title:"Valuation History Report",
      nestedPrime:"_source.value_history",
      innerHits:null,
      fields:[
        {label:"State Parcel No", sortName:"state_parcel_number", fieldName:"state_parcel_number", display:true, export:true, format:"default", order:1},
        {label:"Account No", sortName:"account_number", fieldName:"account_number", display:true, export:true, format:"accountLink", order:2},
        {label:"Tax Year", sortName:"tax_year", fieldName:"value_history.tax_year", display:true, export:true, format:"year", order:3},
        {label:"Total Actual Value", sortName:"actual_value", fieldName:"value_history.actual_value", display:true, export:true, format:"currency", order:4},
        {label:"Total Assessed Value", sortName:"assessed_value", fieldName:"value_history.assessed_value", display:true, export:true, format:"currency", order:5},
        {label:"Taxable Actual Value", sortName:"taxable_value", fieldName:"value_history.taxable_value", display:true, export:true, format:"currency", order:6},
        {label:"Taxable Assessed Value", sortName:"taxable_assessed_value", fieldName:"value_history.taxable_assessed_value", display:true, export:true, format:"currency", order:7}
      ],
      queryStructure:{
        acctIncludes:{
          includes:[
            "account_number"
            ,"primary_address.street_address"  
            , "primary_address.city"
            , "primary_address.zip_code"                   
            , "actual_value_sum"
            , "assessed_value_sum"
            , "state_parcel_number"
            , "value_history.actual_value"
            , "value_history.assessed_value"
            , "value_history.tax_year"
            , "value_history.taxable_assessed_value"
            , "value_history.taxable_value"
            , "primary_owner.name"
            , "geo_location"
          ]
        },
        buildingNested:{nested:{path:"buildings", query:{bool:{filter:[]}}}},
        buildingInnerHits:null,
        usesNested:{nested:{path:"buildings.uses", query:{bool:{filter:[]}}}},
        usesInnerHits:null,
        stylesNested:{nested:{path:"buildings.styles", query:{bool:{filter:[]}}}},
        stylesInnerHits:null,
        salesNested:{nested:{path:"sales", query:{bool:{filter:[]}}}},
        salesInnerHits:null,
        landSegmentsNested:{nested:{path:"land_segments", query:{bool:{filter:[]}}}},
        landSegmentsInnerHits:null
      }
    }
  ];
  activeRpt: IReportDefinition = this.fieldsByReport.filter((x: IReportDefinition) => {
    return x.key === this.defaultRpt;
  })[0];
  selectionList: Array<any> = [
    {label:"Property Ownership & Location Report", value:"PropertyOwnershipAndLocationReport"},
    {label:"Parcel Value Report", value:"ParcelValueReport"},
    {label:"Property Improvement (Occupancy) Report", value:"PropertyImprovementOccupancyReport"},
    {label:"Property Improvement (Segments) Report", value:"PropertyImprovementSegmentsReport"},
    {label:"Building Summary Report", value:"BuildingSummaryReport"},
    {label:"Sales Information Report", value:"SalesInformationReport"},
    {label:"Valuation History Report", value:"ValuationHistoryReport"}
  ];

  constructor() {
  }

  setActiveReport(selected: ISelectedItem): void {
    try {
      this.activeRpt = this.fieldsByReport.filter((x: IReportDefinition) => {
        return x.key === selected.value;
      })[0];

    } catch (e) {
      console.log(e);
    }
  }
}
